import { useEffect, useState } from "react";
import { Bell, MessageSquare } from "lucide-react";
import { NotificationType } from "@repo/share-log-interfaces";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@web/components/ui/card";
import { Switch } from "@web/components/ui/switch";
import { Checkbox } from "@web/components/ui/checkbox";
import { Label } from "@web/components/ui/label";
import { useNotificationSettingStore } from "@web/stores";
import { useWebPush } from "@web/features/web-push/use-web-push";
import { toastService } from "@web/features/toast/toast-service";
import { cn } from "@repo/share-log-web-utils";

interface NotificationSettingSectionProps {
  workspaceId: number;
}

const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  [NotificationType.LOG_CREATED]: "지출/수입 등록",
  [NotificationType.LOG_DELETED]: "지출/수입 삭제",
  [NotificationType.ADJUSTMENT_CREATED]: "정산 생성",
  [NotificationType.ADJUSTMENT_COMPLETED]: "정산 완료",
  [NotificationType.MEMBER_JOINED]: "멤버 참여",
  [NotificationType.MEMBER_LEFT]: "멤버 탈퇴",
  [NotificationType.ROLE_CHANGED]: "권한 변경",
  [NotificationType.WORKSPACE_DELETED]: "워크스페이스 삭제",
  [NotificationType.ADJUSTMENT_REMINDER]: "정산 독려",
  [NotificationType.SPENDING_GOAL_WARNING]: "지출 목표",
  [NotificationType.SPENDING_GOAL_EXCEEDED]: "지출 목표",
  [NotificationType.SPENDING_GOAL_CREATED]: "지출 목표 변경",
  [NotificationType.SPENDING_GOAL_UPDATED]: "지출 목표 변경",
  [NotificationType.SPENDING_GOAL_DELETED]: "지출 목표 변경",
  [NotificationType.ASSET_UPDATE_REMINDER]: "자산 업데이트 알림",
};

// 시스템 배치 알림 — 사용자 설정 UI에 노출하지 않는 타입
const SYSTEM_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.ADJUSTMENT_REMINDER,
  NotificationType.ASSET_UPDATE_REMINDER,
];

// 하나의 UI 토글로 묶어서 제어하는 알림 그룹
// 대표 타입만 UI에 표시하고, 토글 시 그룹 내 모든 타입을 함께 변경
const GROUPED_NOTIFICATION_TYPES: Partial<Record<NotificationType, NotificationType[]>> = {
  [NotificationType.SPENDING_GOAL_WARNING]: [
    NotificationType.SPENDING_GOAL_WARNING,
    NotificationType.SPENDING_GOAL_EXCEEDED,
  ],
  [NotificationType.SPENDING_GOAL_CREATED]: [
    NotificationType.SPENDING_GOAL_CREATED,
    NotificationType.SPENDING_GOAL_UPDATED,
    NotificationType.SPENDING_GOAL_DELETED,
  ],
};

// 그룹의 대표가 아닌 타입 (UI에서 숨김)
const HIDDEN_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.SPENDING_GOAL_EXCEEDED,
  NotificationType.SPENDING_GOAL_UPDATED,
  NotificationType.SPENDING_GOAL_DELETED,
];

export function NotificationSettingSection({
  workspaceId,
}: NotificationSettingSectionProps) {
  const { setting, fetchSetting, updateSetting, status } =
    useNotificationSettingStore();
  const {
    status: pushStatus,
    handleSubscribe,
    handleUnsubscribe,
  } = useWebPush();
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      void fetchSetting(workspaceId);
    }
  }, [workspaceId, fetchSetting]);

  // DB에서 webPushEnabled가 true인데 브라우저 구독이 없으면 자동 구독
  useEffect(() => {
    if (setting?.webPushEnabled && pushStatus === "ready") {
      void handleSubscribe();
    }
  }, [setting?.webPushEnabled, pushStatus, handleSubscribe]);

  const handleWebPushToggle = async (checked: boolean) => {
    setIsUpdating(true);
    try {
      // 활성화 시 브라우저 푸시 구독도 함께 처리
      if (checked && pushStatus === "ready") {
        await handleSubscribe();
      } else if (!checked && pushStatus === "subscribed") {
        await handleUnsubscribe();
      }

      await updateSetting(workspaceId, { webPushEnabled: checked });
      toastService.success(
        checked
          ? "웹 푸시 알림이 활성화되었습니다."
          : "웹 푸시 알림이 비활성화되었습니다.",
      );
    } catch {
      toastService.error("웹 푸시 알림 설정 변경에 실패했습니다.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSlackToggle = async (checked: boolean) => {
    setIsUpdating(true);
    try {
      await updateSetting(workspaceId, { slackEnabled: checked });
      toastService.success(
        checked
          ? "Slack 알림이 활성화되었습니다."
          : "Slack 알림이 비활성화되었습니다.",
      );
    } catch (error) {
      console.error("Failed to update slack setting:", error);
      toastService.error("Slack 알림 설정 변경에 실패했습니다.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTypeToggle = async (type: NotificationType, checked: boolean) => {
    if (!setting) return;

    setIsUpdating(true);
    try {
      // 그룹 타입이면 그룹 내 모든 타입을 함께 토글
      const typesToToggle = GROUPED_NOTIFICATION_TYPES[type] ?? [type];
      let newTypes: NotificationType[];
      if (checked) {
        const toAdd = typesToToggle.filter((t) => !setting.enabledTypes.includes(t));
        newTypes = [...setting.enabledTypes, ...toAdd];
      } else {
        newTypes = setting.enabledTypes.filter((t) => !typesToToggle.includes(t));
      }

      await updateSetting(workspaceId, { enabledTypes: newTypes });
      toastService.success("알림 유형이 변경되었습니다.");
    } catch (error) {
      console.error("Failed to update notification types:", error);
      toastService.error("알림 유형 변경에 실패했습니다.");
    } finally {
      setIsUpdating(false);
    }
  };

  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">알림 설정</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">로딩 중...</p>
        </CardContent>
      </Card>
    );
  }

  if (!setting) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">알림 설정</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            알림 설정을 불러올 수 없습니다.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">알림 설정</CardTitle>
        <CardDescription>
          워크스페이스 이벤트에 대한 알림을 설정합니다.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 알림 채널 */}
        <div className="space-y-4">
          <h4 className="font-medium">알림 채널</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label htmlFor="webPush" className="font-medium">
                    웹 푸시 알림
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    브라우저에서 푸시 알림을 받습니다.
                  </p>
                </div>
              </div>
              <Switch
                id="webPush"
                checked={setting.webPushEnabled}
                onCheckedChange={handleWebPushToggle}
                disabled={isUpdating}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label htmlFor="slack" className="font-medium">
                    Slack 알림
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    프로필에 설정된 Slack 웹훅으로 알림을 받습니다.
                  </p>
                </div>
              </div>
              <Switch
                id="slack"
                checked={setting.slackEnabled}
                onCheckedChange={handleSlackToggle}
                disabled={isUpdating}
              />
            </div>
          </div>
        </div>

        {/* 알림 유형 */}
        <div className="space-y-4">
          <h4 className="font-medium">알림 유형</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.values(NotificationType).filter((type) => !SYSTEM_NOTIFICATION_TYPES.includes(type) && !HIDDEN_NOTIFICATION_TYPES.includes(type)).map((type) => {
              // 그룹 타입이면 그룹 내 모든 타입이 활성화되어 있는지 확인
              const groupTypes = GROUPED_NOTIFICATION_TYPES[type];
              const isChecked = groupTypes
                ? groupTypes.every((t) => setting.enabledTypes.includes(t))
                : setting.enabledTypes.includes(type);
              return (
                <div
                  key={type}
                  className={cn(
                    "flex items-center space-x-2 p-3 rounded-md border",
                    isChecked
                      ? "border-primary/50 bg-primary/5"
                      : "border-border",
                  )}
                >
                  <Checkbox
                    id={type}
                    checked={isChecked}
                    onCheckedChange={(checked: boolean) =>
                      handleTypeToggle(type, checked)
                    }
                    disabled={isUpdating}
                  />
                  <Label
                    htmlFor={type}
                    className="flex-1 cursor-pointer text-sm"
                  >
                    {NOTIFICATION_TYPE_LABELS[type]}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
